import React, {useEffect, useRef, useState} from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Modal,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import NumberInput from "../form/NumberInput"
import AsyncButton from "../button/AsyncButton"
import ErrorMessage from "../error/ErrorMessage"
import {ProjectJson} from "../../api/json/ProjectJson"
import SynthesisSettingsJson from "../../api/json/SynthesisSettingsJson"
import SynthesisResultJson from "../../api/json/SynthesisResultJson"
import CellJson from "../../api/json/CellJson"
import ApiError from "../../api/ApiError"
import {SynthesisRequests} from "../../api/requests/SynthesisRequests"
import {RowRequests} from "../../api/requests/RowRequests"
import {RowDataset} from "../../api/enum/RowDataset"
import {RowOrigin} from "../../api/enum/RowOrigin"

interface SynthesizeRunModalProps {
    project: ProjectJson
    open: boolean
    onClose: () => void
    onAdded: (count: number) => void
}

function SynthesizeRunModal({project, open, onClose, onAdded}: SynthesizeRunModalProps) {
    const [settings, setSettings] = useState<SynthesisSettingsJson>()
    const [loadError, setLoadError] = useState<ApiError>()

    const [alignmentText, setAlignmentText] = useState<string>("")
    const [numberOfRows, setNumberOfRows] = useState<number>(10)

    const [running, setRunning] = useState(false)
    const [savedCount, setSavedCount] = useState(0)
    const [runError, setRunError] = useState<ApiError>()
    const [done, setDone] = useState(false)

    // Use a ref so the cancellation/escape from the modal mid-run is reflected
    // inside the in-flight async loop without restarting it.
    const cancelledRef = useRef(false)

    // Load persisted settings whenever the modal opens. Reset transient state.
    useEffect(() => {
        if (!open) return
        cancelledRef.current = false
        setSavedCount(0)
        setRunError(undefined)
        setDone(false)
        setRunning(false)

        SynthesisRequests.getSettings(project.id)
            .then(s => {
                setSettings(s)
                setAlignmentText(s.alignmentText ?? "")
                setNumberOfRows(s.numberOfRows ?? 10)
                setLoadError(undefined)
            })
            .catch(setLoadError)
    }, [open, project.id])

    const saveResultAsRow = async (result: SynthesisResultJson, validationRatio: number): Promise<void> => {
        const cells: CellJson[] = project.columns.map(column => ({
            column: column,
            value: result.referenceValues[column.reference] ?? "",
        }))
        const dataset = Math.random() < validationRatio ? RowDataset.VALIDATION : RowDataset.TRAINING
        await RowRequests.create(project.id, dataset, RowOrigin.SYNTHESIS, cells, result.id)
    }

    const handleStart = async () => {
        if (!settings) return
        setRunning(true)
        setRunError(undefined)
        setSavedCount(0)
        setDone(false)
        cancelledRef.current = false

        // Persist the user's per-run choices so they're remembered next time.
        try {
            await SynthesisRequests.updateSettings(project.id, {
                ...settings,
                alignmentText: alignmentText,
                numberOfRows: numberOfRows,
            })
        } catch {
            // Non-fatal — settings save failure shouldn't block the run.
        }

        let saved = 0
        try {
            while (saved < numberOfRows && !cancelledRef.current) {
                const remaining = numberOfRows - saved
                const batchSize = Math.min(remaining, settings.batchSize)
                const results = await SynthesisRequests.synthesize(project.id, {
                    ...settings,
                    alignmentText,
                    numberOfRows,
                    batchSize,
                })
                if (cancelledRef.current) break

                for (const result of results) {
                    if (cancelledRef.current) break
                    await saveResultAsRow(result, settings.validationRatio)
                    saved += 1
                    setSavedCount(saved)
                }
            }
            if (!cancelledRef.current) {
                setDone(true)
            }
        } catch (e) {
            setRunError(e as ApiError)
        } finally {
            setRunning(false)
            if (saved > 0) {
                onAdded(saved)
            }
        }
    }

    const handleClose = () => {
        cancelledRef.current = true
        onClose()
    }

    const canStart = !!settings && !running && numberOfRows > 0
    const showProgress = running || (savedCount > 0 && !done)

    return (
        <Modal open={open} onClose={(_, reason) => {
            if (reason !== "backdropClick" || !running) handleClose()
        }}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '92vw',
                maxWidth: 540,
                maxHeight: '90vh',
                backgroundColor: '#f7f7f7',
                borderRadius: '6px',
                outline: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction="row"
                       alignItems="center"
                       justifyContent="space-between"
                       sx={{p: 2, backgroundColor: '#fff'}}>
                    <Typography variant="h3" fontSize={18} fontWeight={500} sx={{color: '#555'}}>
                        Synthesize Rows
                    </Typography>
                </Stack>
                <Divider/>

                <Box sx={{p: 3, overflowY: 'auto', flex: '1 1 auto'}}>
                    {loadError && <ErrorMessage error={loadError}/>}

                    {!settings && !loadError && <CircularProgress size={24}/>}

                    {settings && (
                        <Stack spacing={3}>
                            <TextField label="Alignment text"
                                       helperText='Optional guidance for this run, e.g. "Try to mention cats where possible."'
                                       value={alignmentText}
                                       multiline
                                       minRows={2}
                                       maxRows={6}
                                       disabled={running}
                                       onChange={e => setAlignmentText(e.target.value)}
                            />

                            <NumberInput label="Number of rows"
                                         value={numberOfRows}
                                         min={1}
                                         max={1000}
                                         onBlur={setNumberOfRows}/>

                            {showProgress && (
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <CircularProgress size={18}/>
                                    <Typography variant="body2" color="text.secondary">
                                        Synthesized {savedCount} of {numberOfRows}…
                                    </Typography>
                                </Stack>
                            )}

                            {done && (
                                <Typography variant="body2" sx={{color: 'success.main'}}>
                                    Added {savedCount} row{savedCount === 1 ? '' : 's'} to your dataset.
                                </Typography>
                            )}

                            {runError && <ErrorMessage error={runError}/>}

                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button variant="outlined" color="secondary" onClick={handleClose}>
                                    {done ? 'Close' : 'Cancel'}
                                </Button>
                                {!done &&
                                    <AsyncButton variant="contained"
                                                 waiting={running}
                                                 disabled={!canStart}
                                                 onClick={handleStart}>
                                        Synthesize
                                    </AsyncButton>
                                }
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Box>
        </Modal>
    )
}

export default SynthesizeRunModal
